import React, { useState, useEffect, useRef } from "react";
import { US_STATES, getCitiesForState } from "../../data/us-locations";
import { getCountriesForSearch } from "../../data/countries";

const LocationAutocomplete = ({
  value,
  onChange,
  placeholder,
  type = "state", // "state", "city", or "country"
  stateValue = "", // For city autocomplete, we need to know which state
  state = "", // Alias for stateValue for backward compatibility
  style = {},
  inputStyle = {},
  labelStyle = {},
  label = "",
  autoFocus = false,
  disabled = false,
  inputId,
  openOnFocus = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [hasUserInput, setHasUserInput] = useState(false);
  const wrapperRef = useRef(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setIsFocused(false);
        setHasUserInput(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Use state prop as fallback for stateValue
  const effectiveStateValue = stateValue || state;
  
  // Generate suggestions based on input
  useEffect(() => {
    if (!value || value.length < 1 || disabled) {
      setSuggestions([]);
      return;
    }

    const searchValue = value.toLowerCase();
    let filtered = [];

    if (type === "country") {
      // Filter countries
      filtered = getCountriesForSearch(value);
    } else if (type === "state") {
      // Filter states
      filtered = US_STATES
        .map(s => s.name)
        .filter(name => name.toLowerCase().includes(searchValue))
        .slice(0, 8); // Limit to 8 suggestions
    } else if (type === "city" && effectiveStateValue) {
      // Filter cities for the selected state
      const cities = getCitiesForState(effectiveStateValue) || [];
      filtered = cities
        .filter(city => city.toLowerCase().includes(searchValue))
        .slice(0, 8);
    }

    setSuggestions(filtered);
    // Only show suggestions when focused AND (user has typed OR openOnFocus is true)
    const shouldShow = filtered.length > 0 && isFocused && (hasUserInput || openOnFocus);
    setShowSuggestions(shouldShow);
    setHighlightedIndex(-1);
  }, [value, type, effectiveStateValue, disabled, isFocused, hasUserInput, openOnFocus]);

  const handleInputChange = (e) => {
    setHasUserInput(true);
    onChange(e.target.value);
  };

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion);
    setShowSuggestions(false);
    setHasUserInput(false);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const defaultInputStyle = {
    border: 'none',
    borderBottom: '2px solid rgba(0,0,0,.12)',
    background: 'transparent',
    padding: '8px 0',
    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
    fontSize: '16px',
    color: '#0f084b',
    outline: 'none',
    width: '100%',
    ...inputStyle
  };

  const defaultLabelStyle = {
    fontFamily: "'Sailors', serif",
    fontSize: '16px',
    color: '#0f084b',
    textAlign: 'left',
    marginTop: 6,
    ...labelStyle
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', ...style }}>
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true);
          if (suggestions.length > 0 && !disabled && (hasUserInput || openOnFocus)) {
            setShowSuggestions(true);
          }
        }}
        onBlur={() => {
          setIsFocused(false);
          setHasUserInput(false);
          setShowSuggestions(false);
        }}
        placeholder={placeholder}
        style={defaultInputStyle}
        autoFocus={autoFocus}
        disabled={disabled}
      />
      
      {label && <label style={defaultLabelStyle}>{label}</label>}
      
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid rgba(0,0,0,.12)',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000,
          marginTop: '2px'
        }}>
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSuggestionClick(suggestion);
              }}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                backgroundColor: index === highlightedIndex ? '#f0f0f0' : 'white',
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: '14px',
                color: '#0f084b',
                borderBottom: index < suggestions.length - 1 ? '1px solid rgba(0,0,0,.06)' : 'none'
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
